import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from '@/hooks/useTenantId';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, User, Crown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TenantUser {
  user_id: string;
  role: 'super_admin' | 'tenant_admin' | 'user';
  profile: {
    first_name: string | null;
    last_name: string | null;
    email?: string;
  };
}

export function UserManagement() {
  const { tenantId } = useTenantId();
  const { isTenantAdmin, isLoading: roleLoading } = useUserRole();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['tenant-users', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          profile:user_id (
            first_name,
            last_name
          )
        `)
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return data as unknown as TenantUser[];
    },
    enabled: !!tenantId && isTenantAdmin,
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'user' | 'tenant_admin' }) => {
      if (!tenantId) throw new Error('Tenant not found');

      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Crown className="h-4 w-4" />;
      case 'tenant_admin':
        return <Shield className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "destructive" => {
    switch (role) {
      case 'super_admin':
        return 'destructive';
      case 'tenant_admin':
        return 'default';
      default:
        return 'secondary';
    }
  };

  if (roleLoading || isLoading) {
    return <div>Loading...</div>;
  }

  if (!isTenantAdmin) {
    return (
      <Alert>
        <AlertDescription>
          You don't have permission to manage users. Only tenant administrators can access this feature.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <CardDescription>
          Manage user roles and permissions for your organization
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {users.map((user) => {
            const displayName = user.profile
              ? `${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim() || 'Unnamed User'
              : 'Unnamed User';

            return (
              <div
                key={user.user_id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getRoleIcon(user.role)}
                  <div>
                    <p className="font-medium">{displayName}</p>
                    <Badge variant={getRoleBadgeVariant(user.role)} className="mt-1">
                      {user.role.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                <Select
                  value={user.role}
                  onValueChange={(newRole) =>
                    updateRole.mutate({ userId: user.user_id, newRole: newRole as 'user' | 'tenant_admin' })
                  }
                  disabled={user.role === 'super_admin'}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            );
          })}
          {users.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No users found in your organization
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
